import { useEffect, useMemo, useState } from 'react';
import {
  Loader2,
  RefreshCcw,
  Download,
  Users,
  Send,
  Phone,
  CalendarCheck,
  Home,
  AlertTriangle,
  ShieldCheck,
} from 'lucide-react';
import {
  Tooltip as ReTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useT, useLang, type StringKey } from '@/i18n';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';
import { toast } from '@/stores/toast.store';
import { EmptyState, Skeleton } from '@/components/chassis/States';
import { Pager } from '@/components/chassis/Pager';
import { useQuery } from '@tanstack/react-query';
import { apiCall } from '@/lib/api-client';

import {
  useReportsSummary,
  useReportsTrends,
  useReportsByAgent,
  useReportsByUploader,
  useBookingKpis,
  downloadReportCsv,
  type ByAgentRow,
  type ByUploaderRow,
} from '@/features/admin/reports.api';
import { adminApi, type AdminScreeningsResponse, type AdminFindhelpStatus } from '@/features/admin/admin.api';
import { HouseholdPromotionSection } from './components/HouseholdPromotionSection';
import { CallOutcomesExportSection } from './components/CallOutcomesExportSection';

import { AgentDetailModal } from './components/AgentDetailModal';
import { UploaderDetailModal } from './components/UploaderDetailModal';

// ---------------------------------------------------------------------------
// F5-T6b · Admin 07 — Reportes y exports (contrato 2026-08-28).
//
// El panel del contrato: período FIJO de 4 opciones (el selector de fechas
// libre MURIÓ — dies #5), la serie de screenings por día como barras, los
// KPIs de citas de 30 días, la lista global paginada de 25, y la mesa de
// exports donde cada tarjeta declara sus columnas antes de bajar.
//
// NECESITA-SERVER (dibujado, sin endpoint hoy — NO se finge):
//   · Serie diaria de 6 meses: /admin/reports/trends acepta period 7|30|90.
//     «Este mes» se deriva honesto filtrando la serie de 90 días.
//   · «Reagendadas después del no-show»: booking-kpis no trae ese conteo.
//   · «N positivos / sin necesidades / hogar (N)» por fila de la lista:
//     /admin/screenings/all no trae dominios positivos por screening.
//   · Los 6 exports del dibujo (screenings del período con nombres, usuarios,
//     citas, casos de navegación, serie diaria CSV, niveles y metas): no
//     existen como endpoints. La mesa lista los exports REALES (by-agent,
//     by-uploader, summary — sin pacientes) + el de call outcomes (F3,
//     auditado) que vive en su sección conservada.
//   · Auditoría del export de reportes: /admin/reports/export no audita hoy
//     (no lleva datos de paciente; el chip lo dice honesto).
//
// El botón PDF que devolvía 501 vive solo en el server (format=pdf) — la UI
// nunca lo tuvo; acá no se dibuja (dies #1, decisión con puerta al CEO).
// ---------------------------------------------------------------------------

type PeriodKey = '7d' | '30d' | 'mes' | '6m';

const PERIOD_ORDER: PeriodKey[] = ['7d', '30d', 'mes', '6m'];
const PERIOD_LABEL: Record<PeriodKey, StringKey> = {
  '7d': 'f5t6b.rp.p.7d',
  '30d': 'f5t6b.rp.p.30d',
  mes: 'f5t6b.rp.p.mes',
  '6m': 'f5t6b.rp.p.6m',
};

function isoOf(d: Date): string {
  const y = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
  const da = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
}

function periodRange(k: PeriodKey): { from: string; to: string } {
  const now = new Date();
  const to = isoOf(now);
  if (k === '7d') {
    const d = new Date(now);
    d.setUTCDate(d.getUTCDate() - 6);
    return { from: isoOf(d), to };
  }
  if (k === '30d') {
    const d = new Date(now);
    d.setUTCDate(d.getUTCDate() - 29);
    return { from: isoOf(d), to };
  }
  if (k === 'mes') {
    return { from: isoOf(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))), to };
  }
  // 6m — desde el primero del mes, cinco meses atrás (p.ej. marzo–agosto).
  return { from: isoOf(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 5, 1))), to };
}

const KICKER = 'font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-teal';
const CARD = 'bg-white border-[1.5px] border-nav-card rounded-xl px-6 pt-[22px] pb-6';
const NUM = 'font-head text-[26px] font-extrabold leading-none tracking-[-0.02em] tabular-nums';

const LIST_PAGE_SIZE = 25;

export function ReportsPage(): JSX.Element {
  const t = useT();
  const lang = useLang();
  const locale = lang === 'es' ? 'es-AR' : 'en-US';
  const accessToken = useAuthStore((s) => s.accessToken);

  const [period, setPeriod] = useState<PeriodKey>('30d');
  const { from, to } = useMemo(() => periodRange(period), [period]);
  const [listPage, setListPage] = useState(0);
  useEffect(() => setListPage(0), [period]);

  const [openAgentId, setOpenAgentId] = useState<string | null>(null);
  const [openUploaderId, setOpenUploaderId] = useState<string | null>(null);
  const [exporting, setExporting] = useState<'summary' | 'by-agent' | 'by-uploader' | null>(null);
  const [agentSort, setAgentSort] = useState<{ col: keyof ByAgentRow; dir: 'asc' | 'desc' }>({
    col: 'screeningsSubmitted',
    dir: 'desc',
  });
  const [uploaderSort, setUploaderSort] = useState<{ col: keyof ByUploaderRow; dir: 'asc' | 'desc' }>({
    col: 'uploaded',
    dir: 'desc',
  });

  const summaryQ = useReportsSummary(from, to);
  // La serie del server es rolling 7/30/90; «Este mes» se filtra de la de 90.
  const trendsQ = useReportsTrends('screenings', period === '7d' ? 7 : period === '30d' ? 30 : 90);
  const byAgentQ = useReportsByAgent(from, to);
  const byUploaderQ = useReportsByUploader(from, to);
  const bookingKpisQ = useBookingKpis(30);

  const listQ = useQuery<AdminScreeningsResponse>({
    queryKey: ['f5t6b', 'rp', 'screenings', from, to, listPage],
    queryFn: () =>
      adminApi.listAllScreenings({
        status: 'submitted',
        from,
        to,
        limit: LIST_PAGE_SIZE,
        offset: listPage * LIST_PAGE_SIZE,
        sort: 'newest',
      }),
    staleTime: 30_000,
  });

  const refreshAll = (): void => {
    void summaryQ.refetch();
    void trendsQ.refetch();
    void byAgentQ.refetch();
    void byUploaderQ.refetch();
    void bookingKpisQ.refetch();
    void listQ.refetch();
  };

  // ---- serie de barras ----------------------------------------------------
  const serie = useMemo(() => {
    const raw = trendsQ.data?.data ?? [];
    const rows = raw
      .map((r) => ({ day: String(r.date ?? r.day ?? ''), value: Number(r.submitted ?? 0) }))
      .filter((r) => r.day >= from && r.day <= to);
    return rows;
  }, [trendsQ.data, from, to]);

  const serieTotal = useMemo(() => serie.reduce((acc, r) => acc + r.value, 0), [serie]);
  const serieAvg = serie.length > 0 ? Math.round(serieTotal / serie.length) : 0;
  const serieMax = useMemo(() => Math.max(1, ...serie.map((r) => r.value)), [serie]);

  const rangeLabel = useMemo(() => {
    const f = new Date(`${from}T00:00:00Z`);
    const tt = new Date(`${to}T00:00:00Z`);
    if (period === 'mes') {
      const month = tt.toLocaleDateString(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' });
      return t('f5t6b.rp.range.mes', { month });
    }
    const a = f.toLocaleDateString(locale, { day: 'numeric', month: 'long', timeZone: 'UTC' });
    const b = tt.toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
    return `${a} – ${b}`;
  }, [from, to, period, locale, t]);

  // ---- exports ------------------------------------------------------------
  const handleExport = async (type: 'summary' | 'by-agent' | 'by-uploader'): Promise<void> => {
    if (!accessToken || exporting) return;
    // Export vacío: NO se baja un CSV con sólo encabezados — se avisa antes.
    if (type === 'by-agent' && (byAgentQ.data?.length ?? 0) === 0) {
      toast.info(t('f5t6b.rp.exp.emptyGuard'), '');
      return;
    }
    if (type === 'by-uploader' && (byUploaderQ.data?.length ?? 0) === 0) {
      toast.info(t('f5t6b.rp.exp.emptyGuard'), '');
      return;
    }
    setExporting(type);
    try {
      await downloadReportCsv(type, from, to, accessToken);
    } catch (err) {
      toast.error(t('admin.reports.error.title'), (err as Error).message);
    } finally {
      setExporting(null);
    }
  };

  const sortedAgents = useMemo(() => {
    const arr = byAgentQ.data ? [...byAgentQ.data] : [];
    arr.sort((a, b) => {
      const av = a[agentSort.col];
      const bv = b[agentSort.col];
      if (typeof av === 'number' && typeof bv === 'number') {
        return agentSort.dir === 'asc' ? av - bv : bv - av;
      }
      return agentSort.dir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return arr;
  }, [byAgentQ.data, agentSort]);

  const sortedUploaders = useMemo(() => {
    const arr = byUploaderQ.data ? [...byUploaderQ.data] : [];
    arr.sort((a, b) => {
      const av = a[uploaderSort.col];
      const bv = b[uploaderSort.col];
      if (typeof av === 'number' && typeof bv === 'number') {
        return uploaderSort.dir === 'asc' ? av - bv : bv - av;
      }
      return uploaderSort.dir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return arr;
  }, [byUploaderQ.data, uploaderSort]);

  const outcomeData = useMemo(() => {
    const items = summaryQ.data?.callAttempts.byOutcome ?? [];
    return items.map((it) => ({ name: it.outcome, value: it.count }));
  }, [summaryQ.data]);

  const listTotal = listQ.data?.total ?? 0;
  const listPages = Math.max(1, Math.ceil(listTotal / LIST_PAGE_SIZE));

  return (
    <div className="font-body text-nav-navy max-w-[1100px]">
      <header className="mb-6">
        <p className="font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-slate m-0">
          {t('f5t6b.rp.kicker')}
        </p>
        <h1 className="font-head text-[32px] font-extrabold tracking-[-0.025em] leading-tight mt-1 mb-2">
          {t('f5t6b.rp.title')}
        </h1>
        <p className="text-[15px] leading-relaxed text-nav-navy/90 m-0 max-w-[680px]">{t('f5t6b.rp.lead')}</p>
        <p className="mt-2 text-[13.5px] leading-relaxed text-nav-slate max-w-[680px] m-0">{t('f5t6b.rp.sub')}</p>
      </header>

      <div className="bg-nav-soft border-[1.5px] border-nav-card rounded-[14px] px-7 pt-7 pb-8 animate-fade-up-slow motion-reduce:animate-none">
        {/* Período — 4 fijos, el selector libre murió. */}
        <div className="flex flex-wrap items-center gap-3 mb-5">
          <span className="font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-slate">
            {t('f5t6b.rp.period')}
          </span>
          <div className="inline-flex items-center gap-[2px] bg-white border-[1.5px] border-nav-card rounded-[10px] p-[3px]">
            {PERIOD_ORDER.map((k) => (
              <button
                key={k}
                type="button"
                aria-pressed={period === k}
                onClick={() => setPeriod(k)}
                className={cn(
                  'min-h-[44px] px-4 rounded-lg font-head font-bold text-[13px] transition-colors motion-reduce:transition-none',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2',
                  period === k
                    ? 'bg-nav-navy text-white hover:bg-nav-navyHover'
                    : 'text-nav-slate hover:bg-nav-softer hover:text-nav-navy',
                )}
              >
                {t(PERIOD_LABEL[k])}
              </button>
            ))}
          </div>
          <span className="ml-auto text-[13px] text-nav-slate">{rangeLabel}</span>
        </div>

        {/* aria-live: el cambio de período se anuncia junto al total nuevo.
            En 6m la serie diaria no existe (NECESITA-SERVER) — no se anuncia
            un total que solo cubriría 90 días. */}
        <p aria-live="polite" className="sr-only">
          {period !== '6m'
            ? t('f5t6b.rp.serie.aria', {
                label: t(PERIOD_LABEL[period]),
                total: serieTotal.toLocaleString(locale),
                avg: serieAvg.toLocaleString(locale),
              })
            : t(PERIOD_LABEL[period])}
        </p>

        <div className="grid gap-4 mb-4 [grid-template-columns:repeat(auto-fit,minmax(330px,1fr))]">
          {/* Screenings por día — barras. */}
          <section className={CARD} aria-label={t('f5t6b.rp.serie.kicker')}>
            <p className={cn(KICKER, 'm-0 mb-2')}>{t('f5t6b.rp.serie.kicker')}</p>
            <p className="m-0 mb-4 text-[13px] leading-relaxed text-nav-slate">{t('f5t6b.rp.serie.desc')}</p>

            {trendsQ.isLoading ? (
              <Skeleton className="h-[132px] w-full" />
            ) : period === '6m' ? (
              <EmptyState
                icon={<CalendarCheck className="h-5 w-5" aria-hidden="true" />}
                title={t('f5t6b.rp.serie.na6mTitle')}
                body={t('f5t6b.rp.serie.na6mBody')}
              />
            ) : (
              <div className="flex items-end gap-[6px] h-[132px]">
                {serie.map((r) => {
                  const h = Math.max(3, Math.round((r.value / serieMax) * 104));
                  const low = r.value < serieMax * 0.35;
                  const tick = r.day.slice(8, 10);
                  return (
                    <div key={r.day} className="flex-1 min-w-0 flex flex-col items-center justify-end h-full" title={`${r.day}: ${r.value}`}>
                      <div
                        className={cn(
                          'w-full rounded-t-[3px] transition-[height] duration-300 ease-[cubic-bezier(.4,0,.2,1)] motion-reduce:transition-none',
                          low ? 'bg-nav-faint' : 'bg-nav-teal',
                        )}
                        style={{ height: `${h}px` }}
                      />
                      <span className="mt-[7px] text-[10px] text-nav-faint tabular-nums whitespace-nowrap overflow-hidden">
                        {tick}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {period !== '6m' && (
              <div className="flex gap-6 border-t border-nav-hair pt-[18px] mt-4">
                <div>
                  <p className={cn(NUM, 'm-0')}>{serieTotal.toLocaleString(locale)}</p>
                  <p className="m-0 mt-[5px] text-[12.5px] text-nav-slate">{t('f5t6b.rp.serie.total')}</p>
                </div>
                <div>
                  <p className={cn(NUM, 'm-0')}>{serieAvg.toLocaleString(locale)}</p>
                  <p className="m-0 mt-[5px] text-[12.5px] text-nav-slate">{t('f5t6b.rp.serie.avg')}</p>
                </div>
              </div>
            )}
          </section>

          {/* Citas · KPIs de 30 días. */}
          <section className={CARD} aria-label={t('f5t6b.rp.citas.kicker')}>
            <p className={cn(KICKER, 'm-0 mb-2')}>{t('f5t6b.rp.citas.kicker')}</p>
            <p className="m-0 mb-4 text-[13px] leading-relaxed text-nav-slate">{t('f5t6b.rp.citas.desc')}</p>
            {bookingKpisQ.isLoading ? (
              <div className="space-y-3" aria-busy="true">
                <Skeleton className="h-[38px] w-full" />
                <Skeleton className="h-[38px] w-full" />
                <Skeleton className="h-[38px] w-full" />
              </div>
            ) : bookingKpisQ.data ? (
              (() => {
                const k = bookingKpisQ.data;
                const total = k.completed + k.missed + k.cancelled + k.scheduled;
                const rows: Array<{ label: string; value: number; pct: number }> = [
                  { label: t('f5t6b.rp.citas.agendadas'), value: total, pct: total > 0 ? 100 : 0 },
                  {
                    label: t('f5t6b.rp.citas.atendidas'),
                    value: k.completed,
                    pct: total > 0 ? Math.round((k.completed / total) * 100) : 0,
                  },
                  {
                    label: t('f5t6b.rp.citas.noshow'),
                    value: k.missed,
                    pct: total > 0 ? Math.round((k.missed / total) * 100) : 0,
                  },
                  // NECESITA-SERVER: «Reagendadas después del no-show» — el
                  // endpoint booking-kpis no trae ese conteo.
                ];
                return (
                  <div className="flex flex-col gap-[13px]">
                    {rows.map((r) => (
                      <div key={r.label}>
                        <div className="flex items-baseline justify-between mb-[5px]">
                          <span className="text-[13px]">{r.label}</span>
                          <span className="font-head text-[13.5px] font-bold tabular-nums">
                            {r.value.toLocaleString(locale)} ({r.pct}%)
                          </span>
                        </div>
                        <div className="h-[6px] rounded-[3px] bg-nav-hair overflow-hidden">
                          <div
                            className="h-full rounded-[3px] bg-nav-teal transition-[width] duration-300 ease-[cubic-bezier(.4,0,.2,1)] motion-reduce:transition-none"
                            style={{ width: `${r.pct}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()
            ) : null}
          </section>
        </div>

        {/* Screenings del período — lista global paginada de 25. */}
        <section className={cn(CARD, 'mb-4')} aria-label={t('f5t6b.rp.lista.kicker')}>
          <p className={cn(KICKER, 'm-0 mb-2')}>{t('f5t6b.rp.lista.kicker')}</p>
          <p className="m-0 mb-4 text-[13px] leading-relaxed text-nav-slate">{t('f5t6b.rp.lista.desc')}</p>

          {listQ.isLoading ? (
            <div className="space-y-2" aria-busy="true">
              {Array.from({ length: 5 }, (_, i) => (
                <Skeleton key={i} className="h-[62px] w-full" />
              ))}
            </div>
          ) : (listQ.data?.items.length ?? 0) === 0 ? (
            <EmptyState
              icon={<Send className="h-5 w-5" aria-hidden="true" />}
              title={t('f5t6b.rp.lista.emptyTitle')}
              body={t('f5t6b.rp.lista.emptyBody')}
              action={
                period !== 'mes'
                  ? { label: t('f5t6b.rp.lista.seeMonth'), onClick: () => setPeriod('mes') }
                  : undefined
              }
            />
          ) : (
            <>
              <ul className="m-0 p-0 list-none border-[1.5px] border-nav-card rounded-[10px] overflow-hidden">
                {(listQ.data?.items ?? []).map((row) => (
                  <li
                    key={row.id}
                    className="flex items-center gap-3.5 min-h-[62px] px-4 py-2.5 border-b border-nav-hair last:border-b-0 bg-white"
                  >
                    <span className="flex-1 min-w-0">
                      <span className="block font-head text-[13.5px] font-bold truncate">
                        {row.clientLastName}
                      </span>
                      <span className="block text-[12.5px] text-nav-slate tabular-nums truncate mt-0.5">
                        {/* NECESITA-SERVER: «N positivos / sin necesidades /
                            hogar (N)» — el endpoint no trae dominios. */}
                        ID ****{row.cinHashSuffix} · {row.agentName}
                        {row.submittedAt
                          ? ` · ${new Date(row.submittedAt).toLocaleString(locale, {
                              day: '2-digit',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}`
                          : ''}
                      </span>
                    </span>
                    <FindhelpChip status={row.findhelpStatus} />
                  </li>
                ))}
              </ul>
              <p className="m-0 mt-3.5 text-[13px] text-nav-slate tabular-nums">
                {t('f5t6b.rp.lista.page', {
                  p: listPage + 1,
                  tp: listPages,
                  total: listTotal.toLocaleString(locale),
                })}
              </p>
              <Pager
                page={listPage}
                pages={listPages}
                onPage={setListPage}
                busy={listQ.isFetching}
                prevLabel={t('f5t6b.au.pager.prev')}
                nextLabel={t('f5t6b.au.pager.next')}
                pageLabel={t('f5t6b.au.pager.pageOf', { p: listPage + 1, tp: listPages })}
              />
            </>
          )}
        </section>

        {/* Mesa de exports — cada tarjeta declara sus columnas. */}
        <section className={CARD} aria-label={t('f5t6b.rp.exp.kicker')}>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
            <p className={cn(KICKER, 'm-0')}>{t('f5t6b.rp.exp.kicker')}</p>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-nav-teal/30 bg-nav-tealTint px-[11px] py-[5px] font-head text-[11.5px] font-bold text-nav-tealDark">
              <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
              {t('f5t6b.rp.exp.chip')}
            </span>
          </div>
          <p className="m-0 mb-4 max-w-[680px] text-[13.5px] leading-relaxed text-nav-slate">
            {t('f5t6b.rp.exp.desc')}
          </p>
          <div className="grid gap-3.5 [grid-template-columns:repeat(auto-fit,minmax(300px,1fr))]">
            <ExportCard
              name={t('f5t6b.rp.exp.byagent.name')}
              cols={t('f5t6b.rp.exp.byagent.cols')}
              tag={t('f5t6b.rp.exp.tag.staff')}
              busy={exporting === 'by-agent'}
              disabled={exporting !== null || !accessToken}
              onDownload={() => void handleExport('by-agent')}
            />
            <ExportCard
              name={t('f5t6b.rp.exp.byuploader.name')}
              cols={t('f5t6b.rp.exp.byuploader.cols')}
              tag={t('f5t6b.rp.exp.tag.staff')}
              busy={exporting === 'by-uploader'}
              disabled={exporting !== null || !accessToken}
              onDownload={() => void handleExport('by-uploader')}
            />
            <ExportCard
              name={t('f5t6b.rp.exp.summary.name')}
              cols={t('f5t6b.rp.exp.summary.cols')}
              tag={t('f5t6b.rp.exp.tag.agg')}
              busy={exporting === 'summary'}
              disabled={exporting !== null || !accessToken}
              onDownload={() => void handleExport('summary')}
            />
          </div>
        </section>
      </div>

      {/* CONSERVA (E-23): todo lo vivo que el contrato ni dibuja ni mata —
          KPIs del período, tablas por agente/uploader con drill-down, donut
          de outcomes, export de call outcomes (F3, con su gate y su audit
          tal cual) y household promotion. Solo cambió el marco. */}
      <section className="mt-8">
        <div className="flex items-center justify-between gap-3 mb-3">
          <p className="font-head text-[10.5px] font-bold tracking-[0.14em] uppercase text-nav-slate m-0">
            {t('f5t6b.rp.conserved')}
          </p>
          <Button variant="secondary" size="sm" onClick={refreshAll} disabled={summaryQ.isFetching}>
            <RefreshCcw className={cn('h-4 w-4 mr-1', summaryQ.isFetching && 'animate-spin motion-reduce:animate-none')} />
            {t('admin.uploaderTeam.refresh')}
          </Button>
        </div>

        <div className="flex flex-col gap-6">
          <ExhaustedSection t={t} />
          <CallOutcomesExportSection />

          {summaryQ.error && (
            <Card className="p-4 border-rose-200 bg-rose-50">
              <p className="text-sm text-rose-900">
                {t('admin.reports.error.title')}: {(summaryQ.error as Error).message}
              </p>
            </Card>
          )}

          {summaryQ.isLoading && !summaryQ.data && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" aria-busy="true">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))}
            </div>
          )}

          {summaryQ.data && (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <KpiTile
                icon={<Send className="h-4 w-4" />}
                label={t('admin.reports.kpi.screeningsSubmitted')}
                value={summaryQ.data.screenings.submitted}
                sub={`${summaryQ.data.screenings.uploadedPercent}% ${t('admin.reports.kpi.uploadedSub')}`}
              />
              <KpiTile
                icon={<CalendarCheck className="h-4 w-4" />}
                label={t('admin.reports.kpi.bookingsCompleted')}
                value={summaryQ.data.bookings.completed}
                sub={`${summaryQ.data.bookings.missedPercent}% ${t('admin.reports.kpi.missedSub')}`}
              />
              <KpiTile
                icon={<Home className="h-4 w-4" />}
                label={t('admin.reports.kpi.householdCaptured')}
                value={summaryQ.data.household.captured}
                sub={`${summaryQ.data.household.withMedicaidConfirmed} ${t('admin.reports.kpi.householdMedSub')}`}
              />
              <KpiTile
                icon={<Users className="h-4 w-4" />}
                label={t('admin.reports.kpi.activeAgents')}
                value={summaryQ.data.agents.activeCount}
                sub={`${summaryQ.data.agents.avgScreeningsPerAgent} ${t('admin.reports.kpi.agentAvgSub')}`}
              />
              <KpiTile
                icon={<AlertTriangle className="h-4 w-4" />}
                label={t('admin.reports.kpi.uploadSLA')}
                value={summaryQ.data.uploaders.slaBreaches24h}
                sub={t('admin.reports.kpi.slaSub')}
              />
              <KpiTile
                icon={<Phone className="h-4 w-4" />}
                label={t('admin.reports.kpi.callAttempts')}
                value={summaryQ.data.callAttempts.total}
                sub={`${
                  summaryQ.data.callAttempts.total > 0
                    ? Math.round(
                        (summaryQ.data.callAttempts.successful / summaryQ.data.callAttempts.total) * 100,
                      )
                    : 0
                }% ${t('admin.reports.kpi.callsSuccessSub')}`}
              />
            </div>
          )}

          {/* By agent */}
          <Card className="overflow-hidden">
            <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-ink-900 flex items-center gap-2">
                <Users className="h-4 w-4 text-nav-teal" />
                {t('admin.reports.byAgent.title')}
              </h2>
              <span className="text-xs text-ink-500 tabular-nums">{byAgentQ.data?.length ?? 0}</span>
            </div>
            {byAgentQ.isLoading ? (
              <div className="p-5 space-y-2" aria-busy="true">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : sortedAgents.length === 0 ? (
              <div className="px-5 py-6">
                <EmptyState
                  icon={<Users className="h-5 w-5" aria-hidden="true" />}
                  title={t('admin.reports.empty.title')}
                  body={t('admin.reports.empty.subtitle')}
                />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 text-ink-600 text-[11px] uppercase tracking-wide">
                    <tr>
                      <ThSort label={t('admin.reports.byAgent.col.agent')} col="agentName" sort={agentSort} setSort={setAgentSort} />
                      <ThSort label={t('admin.reports.byAgent.submitted')} col="screeningsSubmitted" sort={agentSort} setSort={setAgentSort} numeric />
                      <ThSort label={t('admin.reports.byAgent.uploaded')} col="screeningsUploaded" sort={agentSort} setSort={setAgentSort} numeric />
                      <ThSort label={t('admin.reports.byAgent.uploadRate')} col="uploadRate" sort={agentSort} setSort={setAgentSort} numeric />
                      <ThSort label={t('admin.reports.byAgent.calls')} col="callAttempts" sort={agentSort} setSort={setAgentSort} numeric />
                      <ThSort label={t('admin.reports.byAgent.avgCall')} col="avgCallSeconds" sort={agentSort} setSort={setAgentSort} numeric />
                      <ThSort label={t('admin.reports.byAgent.missed')} col="missedBookings" sort={agentSort} setSort={setAgentSort} numeric />
                      <ThSort label={t('admin.reports.byAgent.household')} col="householdMembersCaptured" sort={agentSort} setSort={setAgentSort} numeric />
                      <ThSort label={t('admin.reports.byAgent.tier')} col="tier" sort={agentSort} setSort={setAgentSort} />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {sortedAgents.map((a) => (
                      <tr
                        key={a.agentId}
                        className="hover:bg-nav-softer cursor-pointer"
                        role="button"
                        tabIndex={0}
                        onClick={() => setOpenAgentId(String(a.agentId))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setOpenAgentId(String(a.agentId));
                          }
                        }}
                      >
                        <td className="px-4 py-2.5 font-medium text-ink-900">{a.agentName}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{a.screeningsSubmitted}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{a.screeningsUploaded}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{a.uploadRate}%</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{a.callAttempts}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{a.avgCallSeconds}s</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{a.missedBookings}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{a.householdMembersCaptured}</td>
                        <td className="px-4 py-2.5">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-nav-card bg-nav-hair text-nav-slate font-head text-[11px] font-bold">
                            {t(`admin.reports.tier.${a.tier === 'below_bronze' ? 'below' : a.tier}` as StringKey)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* By uploader */}
          <Card className="overflow-hidden">
            <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-ink-900 flex items-center gap-2">
                <Send className="h-4 w-4 text-nav-teal" />
                {t('admin.reports.byUploader.title')}
              </h2>
              <span className="text-xs text-ink-500 tabular-nums">{byUploaderQ.data?.length ?? 0}</span>
            </div>
            {byUploaderQ.isLoading ? (
              <div className="p-5 space-y-2" aria-busy="true">
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </div>
            ) : sortedUploaders.length === 0 ? (
              <div className="px-5 py-6">
                <EmptyState
                  icon={<Send className="h-5 w-5" aria-hidden="true" />}
                  title={t('admin.reports.empty.title')}
                  body={t('admin.reports.empty.subtitle')}
                />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 text-ink-600 text-[11px] uppercase tracking-wide">
                    <tr>
                      <ThSort label={t('admin.reports.byUploader.col.uploader')} col="uploaderName" sort={uploaderSort} setSort={setUploaderSort} />
                      <ThSort label={t('admin.reports.byUploader.uploaded')} col="uploaded" sort={uploaderSort} setSort={setUploaderSort} numeric />
                      <ThSort label={t('admin.reports.byUploader.flagged')} col="flagged" sort={uploaderSort} setSort={setUploaderSort} numeric />
                      <ThSort label={t('admin.reports.byUploader.noMedicaid')} col="noMedicaid" sort={uploaderSort} setSort={setUploaderSort} numeric />
                      <ThSort label={t('admin.reports.byUploader.avgTime')} col="avgTimeToUploadHours" sort={uploaderSort} setSort={setUploaderSort} numeric />
                      <ThSort label={t('admin.reports.byUploader.slaBreaches')} col="slaBreaches" sort={uploaderSort} setSort={setUploaderSort} numeric />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone-100">
                    {sortedUploaders.map((u) => (
                      <tr
                        key={u.uploaderId}
                        className="hover:bg-nav-softer cursor-pointer"
                        role="button"
                        tabIndex={0}
                        onClick={() => setOpenUploaderId(String(u.uploaderId))}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setOpenUploaderId(String(u.uploaderId));
                          }
                        }}
                      >
                        <td className="px-4 py-2.5 font-medium text-ink-900">{u.uploaderName}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{u.uploaded}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{u.flagged}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{u.noMedicaid}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{u.avgTimeToUploadHours}h</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">
                          {u.slaBreaches > 0 ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-nav-amber/60 bg-nav-amberTint text-nav-amberText font-head text-[11px] font-bold">
                              {u.slaBreaches}
                            </span>
                          ) : (
                            <span className="text-ink-400">0</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Outcomes donut */}
          {summaryQ.data && summaryQ.data.callAttempts.total > 0 && (
            <Card className="p-5">
              <h2 className="text-sm font-semibold text-ink-900 mb-4 flex items-center gap-2">
                <Phone className="h-4 w-4 text-nav-teal" />
                {t('admin.reports.outcomes.title')}
              </h2>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={outcomeData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={50}
                      outerRadius={85}
                      paddingAngle={2}
                      label={(props: { value?: number }) => String(props.value ?? '')}
                      labelLine={false}
                    >
                      {outcomeData.map((entry) => (
                        <Cell key={entry.name} fill={OUTCOME_COLOR[entry.name] ?? '#a9b6c1'} />
                      ))}
                    </Pie>
                    <Legend
                      formatter={(name: string) => {
                        const key = `admin.reports.outcomes.${name}` as StringKey;
                        const txt = t(key);
                        return txt === key ? name : txt;
                      }}
                      wrapperStyle={{ fontSize: 11 }}
                    />
                    <ReTooltip
                      formatter={((value: unknown, name: unknown) => {
                        const key = `admin.reports.outcomes.${String(name)}` as StringKey;
                        const txt = t(key);
                        return [value as number, txt === key ? String(name) : txt];
                      }) as never}
                      contentStyle={{
                        fontSize: 12,
                        border: '1px solid #E7E5E4',
                        borderRadius: 6,
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}

          {/* Cluster H — antes vivía DENTRO del empty-state de las tablas (solo
              se veía con datos vacíos, defecto evidente); acá respira sola. */}
          <HouseholdPromotionSection />
        </div>
      </section>

      {/* Modals */}
      <AgentDetailModal agentId={openAgentId} onClose={() => setOpenAgentId(null)} />
      <UploaderDetailModal uploaderId={openUploaderId} onClose={() => setOpenUploaderId(null)} />
    </div>
  );
}

// ----- subcomponents -------------------------------------------------------

const OUTCOME_COLOR: Record<string, string> = {
  no_answer: '#a9b6c1',
  voicemail: '#8fc3c4',
  refused: '#8f2b21',
  language_barrier: '#d9a540',
  answered_completed: '#a7c3a0',
  answered_partial: '#228a8d',
  callback_requested: '#1a6a6d',
  wrong_number: '#8f2b21',
  disconnected: '#8f2b21',
  deceased: '#6b7c8c',
};

function FindhelpChip({ status }: { status: AdminFindhelpStatus }): JSX.Element {
  const t = useT();
  const tone =
    status === 'uploaded'
      ? 'bg-nav-sageTint text-nav-sageText border-nav-sage'
      : status === 'pending'
        ? 'bg-nav-amberTint text-nav-amberText border-nav-amber/60'
        : 'bg-nav-hair text-nav-slate border-nav-card';
  const label =
    status === 'uploaded'
      ? t('f5t6b.rp.st.uploaded')
      : status === 'pending'
        ? t('f5t6b.rp.st.pending')
        : status === 'flagged'
          ? t('f5t6b.rp.st.flagged')
          : t('f5t6b.rp.st.no_medicaid');
  return (
    <span className={cn('shrink-0 rounded-full border px-[11px] py-1 font-head text-[11.5px] font-bold', tone)}>
      {label}
    </span>
  );
}

function ExportCard({
  name,
  cols,
  tag,
  busy,
  disabled,
  onDownload,
}: {
  name: string;
  cols: string;
  tag: string;
  busy: boolean;
  disabled: boolean;
  onDownload: () => void;
}): JSX.Element {
  const t = useT();
  return (
    <div className="bg-nav-softer border-[1.5px] border-nav-card rounded-xl px-5 pt-[18px] pb-5">
      <div className="flex items-start justify-between gap-2 mb-2">
        <p className="m-0 font-head text-[14px] font-bold tracking-[-0.01em]">{name}</p>
        <span className="shrink-0 rounded-full border border-nav-card bg-nav-hair px-[10px] py-1 font-head text-[10.5px] font-bold text-nav-slate">
          {tag}
        </span>
      </div>
      <p className="m-0 mb-3.5 text-[12.5px] leading-relaxed text-nav-slate">{cols}</p>
      <button
        type="button"
        onClick={onDownload}
        disabled={disabled}
        className={cn(
          'inline-flex items-center gap-2 min-h-[44px] px-4 rounded-lg border-[1.5px] border-nav-line bg-white font-head text-[13px] font-bold text-nav-navy',
          'hover:bg-nav-tealTint hover:border-nav-teal hover:text-nav-tealDark transition-colors motion-reduce:transition-none',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal focus-visible:ring-offset-2',
          'disabled:opacity-50 disabled:cursor-not-allowed',
        )}
      >
        {busy ? (
          <>
            <Loader2 className="h-[15px] w-[15px] animate-spin motion-reduce:animate-none" aria-hidden="true" />
            {t('f5t6b.rp.exp.preparing')}
          </>
        ) : (
          <>
            <Download className="h-[15px] w-[15px]" aria-hidden="true" />
            {t('f5t6b.rp.exp.download')}
          </>
        )}
      </button>
    </div>
  );
}

interface KpiTileProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  sub: string;
}

function KpiTile({ icon, label, value, sub }: KpiTileProps): JSX.Element {
  return (
    <Card className="rounded-xl p-4 border-[1.5px] border-nav-card bg-white">
      <div className="flex items-start justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wide text-nav-slate font-medium">{label}</div>
        <div className="h-7 w-7 rounded-lg flex items-center justify-center bg-nav-tealTint text-nav-tealDark">
          {icon}
        </div>
      </div>
      <div className="mt-2 text-2xl font-semibold text-nav-navy tabular-nums">{value.toLocaleString()}</div>
      <div className="text-xs mt-1 text-nav-slate">{sub}</div>
    </Card>
  );
}

interface ThSortProps<T> {
  label: string;
  col: keyof T;
  sort: { col: keyof T; dir: 'asc' | 'desc' };
  setSort: (s: { col: keyof T; dir: 'asc' | 'desc' }) => void;
  numeric?: boolean;
}

function ThSort<T>({ label, col, sort, setSort, numeric }: ThSortProps<T>): JSX.Element {
  const active = sort.col === col;
  return (
    <th
      scope="col"
      className={cn(
        'px-4 py-2 cursor-pointer select-none',
        numeric ? 'text-right' : 'text-left',
        active ? 'text-nav-tealDark' : '',
      )}
      onClick={() =>
        setSort({
          col,
          dir: active && sort.dir === 'desc' ? 'asc' : 'desc',
        })
      }
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active && <span aria-hidden>{sort.dir === 'asc' ? '▲' : '▼'}</span>}
      </span>
    </th>
  );
}

/**
 * R2 (caminata CEO 2026-08-28) — «Intentos agotados»: la lista visible de
 * los pacientes que llegaron al tope (config max_call_attempts). El motor ya
 * los saca de la cola del agente, del take-next y de la reasignacion; aca el
 * admin los VE. Sin PHI (hash + conteos); la identidad completa baja por el
 * export F3 de siempre (auditado), que ya tiene el filtro de agotados.
 */
function ExhaustedSection({ t }: { t: ReturnType<typeof useT> }): JSX.Element {
  const [page, setPage] = useState(0);
  const LIM = 25;
  const q = useQuery({
    queryKey: ['admin', 'reports', 'exhausted', page],
    queryFn: () =>
      apiCall<{
        maxCallAttempts: number;
        total: number;
        rows: Array<{
          clientId: string; cinHashTail: string; attemptCount: number;
          lastAttemptAt: string | null; agentName: string | null; assignmentStatus: string | null;
        }>;
      }>(`/admin/reports/exhausted?limit=${LIM}&offset=${page * LIM}`),
    staleTime: 60_000,
  });
  const total = q.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / LIM));
  return (
    <section className="bg-white border-[1.5px] border-nav-card rounded-[12px] px-6 py-5" data-r2="exhausted">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1.5">
        <h2 className="font-head text-[15px] font-bold text-nav-navy m-0">
          {t('admin.exhausted.title', { n: total })}
        </h2>
        {q.data && (
          <span className="text-[12px] text-nav-slate">
            {t('admin.exhausted.capNote', { max: q.data.maxCallAttempts })}
          </span>
        )}
      </div>
      <p className="text-[12.5px] text-nav-slate m-0 mb-3">{t('admin.exhausted.body')}</p>
      {q.isLoading && <Skeleton className="h-20 w-full" />}
      {!q.isLoading && total === 0 && (
        <p className="text-[13px] text-nav-faint m-0">{t('admin.exhausted.empty')}</p>
      )}
      {total > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[10.5px] uppercase tracking-[.08em] text-nav-slate bg-nav-softer">
                <th className="px-3 py-2 font-head">ID</th>
                <th className="px-3 py-2 font-head">{t('admin.exhausted.colAttempts')}</th>
                <th className="px-3 py-2 font-head">{t('admin.exhausted.colLast')}</th>
                <th className="px-3 py-2 font-head">{t('admin.exhausted.colAgent')}</th>
              </tr>
            </thead>
            <tbody>
              {(q.data?.rows ?? []).map((r: { clientId: string; cinHashTail: string; attemptCount: number; lastAttemptAt: string | null; agentName: string | null }) => (
                <tr key={r.clientId} className="border-b border-nav-hair">
                  <td className="px-3 py-2 tabular-nums">…{r.cinHashTail}</td>
                  <td className="px-3 py-2 tabular-nums">{r.attemptCount}</td>
                  <td className="px-3 py-2">
                    {r.lastAttemptAt ? new Date(r.lastAttemptAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-3 py-2">{r.agentName ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {pages > 1 && (
            <div className="flex items-center justify-between mt-2">
              <Button variant="secondary" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                {t('admin.exhausted.prev')}
              </Button>
              <span className="text-[12px] text-nav-slate tabular-nums">{page + 1} / {pages}</span>
              <Button variant="secondary" size="sm" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>
                {t('admin.exhausted.next')}
              </Button>
            </div>
          )}
        </div>
      )}
      <p className="text-[11.5px] text-nav-faint m-0 mt-3">{t('admin.exhausted.exportNote')}</p>
    </section>
  );
}
